
// **************************************************
//
// IMPORTS
//
// **************************************************

// GAME
import Raf from 'src/helpers/Raf'
import $ from "jquery"
import store from 'src/store'
import { TweenLite } from 'gsap'
import {clamp} from 'src/helpers/MathUtils'
import EventEmitter from 'src/helpers/EventEmitter'
import EventObserver from 'src/helpers/EventObserver'
import {TAU, getColorAverage, getColor, SColor, rotatePoint2D, getDistance2D, getLerp, getLerpP,  getLerp2D, rgbToHex} from "./Utils"
import {default as FaceGeoModel} from 'src/services/FaceFeatureService/FaceGeoModel'
import { ML_MODEL_KEYS, EVENTS as ML_EVENTS, default as AutoMLService} from 'src/services/FaceFeatureService/AutoMLService'

import {AssetList,  AssetListMasks} from 'src/services/FaceFeatureService/data/AssetList'
import HairAssetModel from 'src/services/FaceFeatureService/HairAssetModel'


// **************************************************

const useAutoML = true
const useAutoMLHair = true
const useAutoMLHat = false
const useAutoMLGlasses = false

export const STATE = {
  NO_DATA:"NO_DATA",
  WAITING:"WAITING",
  DATA:"DATA",
}

export const EVENTS = {
  faceInfo:"faceInfo",
  xmlString:"xmlString",
  faceThumbSrc:"faceThumbSrc",
  state:"state"
}

// !! TESTING ONLY !!
const API = {
  KEY:"d45fd466-51e2-4701-8da8-04351c872236",
  SECRET:"171e8465-f548-401d-b63b-caf0dc28df5f"
}

let f=function(){}
let fa={}
let fg={}


class FaceFeatureService {

  get EVENTS() {return EVENTS}

  get STATE(){return STATE}

  constructor () {
    //mixins
    Object.assign(this, EventEmitter)
    Object.assign(this, EventObserver)

    AutoMLService.on(ML_EVENTS.CURRENT_DETECTIONS_READY, this.onMLDetectionReady)

    this.useAutoML = useAutoML
    this.state = STATE.NO_DATA

   // let t = templatePointsRectUnit
    this.faceGeoModel = FaceGeoModel
    this.hairAssetModel = HairAssetModel

    if(window.location.href.indexOf('testassets') > -1){this.testAssetList()}

    //this.data = SceneData
    this.faceImage = {
      imageData:null,
      width:10,
      height:10
    }
    this.faceThumbSrc = ""
    this.faceInfo = {}
    this.xmlString = ""


    let self = this

    let r = {x:2480, y:3508}
    this.transformPoints = {
      rect:r,
      mouth:{x:0.5, y:0.707},
      face_shape:{x:0.5, y:0.1448},

      eye_center:{x:0.5,y:1536/r.y},

      eyes:{x:(r.x-1070)/r.x,y:1536/r.y},
      eyes_right:{x:(r.x-1070)/r.x,y:1536/r.y}, //780, 1335

      eye_brows:{x:(2480-1097)/r.x, y:1404/r.y},
      eye_brows_right:{x:(2480-1097)/r.x, y:1404/r.y},
      nose:{x:0.5,y:1900/r.y},
      facial_hair:{x:0.5,y:2538/r.y},
      facial_hair_shadow:{x:0.5,y:2538/r.y},
    }

    /*
    TODO custom:
    chin-height
    eye-scale
    eye-brow position
    eye_brow-angle
    eye_brow-arch (low mid high)
    eye_brow-arch-center (inner mid outer)
    eye_brow-arch-sharpness (curved, average, sharp)
     */
    this.faceGeometry = {
      get info(){return self.faceInfo || {}},
      get points(){return this.info.geo || {} },
      get faceHeight(){
        let t= this.points['forehead_middle']
        let b = this.points['chin_bottom']
        return getDistance2D(t.x, t.y,  b.x,b.y )
      },

      get faceWidth(){
        let t= this.points['temple_left_3']
        let b = this.points['temple_right_3']
        return getDistance2D(t.x, t.y,  b.x,b.y )
      },

      //normalise face using distance between tearducts, chin-center
      get eyesSpanWidth(){
        //let h = this.faceHeight
        let t = this.points['eye_left_inner']
        let b = this.points['eye_right_inner']
        return  getDistance2D(t.x, t.y,  b.x,b.y )
      },

      get eyesSpanCenterPoint(){
        //let h = this.faceHeight
        let t = this.points['eye_left_inner']
        let b = this.points['eye_right_inner']
        return  getLerp2D(t.x, t.y,  b.x, b.y, 0.5 )
      },

      get chinToEyesCenterHeight(){
        //let h = this.faceHeight
        let t = this.eyesSpanCenterPoint
        let b = this.points['chin_bottom']
        return  getLerp2D(t.x, t.y,  b.x, b.y, 0.5 )
      },

      get eyeToBrowHeight(){
        let t = this.points['eye_left_inner']
        let b = this.points['eyebrow_left_inner']
        let hL = getDistance2D(t.x, t.y,  b.x,b.y )

        t = this.points['eye_right_inner']
        b = this.points['eyebrow_right_inner']
        let hR = getDistance2D(t.x, t.y,  b.x,b.y )

        return getLerp(hR, hL, 0.5 )

      },

      get eyeToBrowHeightUnit(){
        let h = this.faceHeight
        let ebh = this.eyeToBrowHeight
        return ebh/h
      },


      get eyeHeightUnit(){
        let h = this.faceHeight
        let t = this.points['eye_left_top']
        let b = this.points['eye_left_bottom']
        let leh = getDistance2D(t.x, t.y,  b.x,b.y )

        t = this.points['eye_right_top']
        b = this.points['eye_right_bottom']
        let reh = getDistance2D(t.x, t.y,  b.x,b.y )

        let eh = Math.max(leh, reh)

        return eh/h
      },

      get lipTopHeightUnit(){
        let h = this.faceHeight
        let t = this.points['mouth_top']
        let b = this.points['mouth_inner_top']
        let lh = getDistance2D(t.x, t.y,  b.x,b.y )
        return lh/h
      },

      get lipBaseHeightUnit(){
        let h = this.faceHeight
        let t = this.points['mouth_inner_bottom']
        let b = this.points['mouth_bottom']
        let lh = getDistance2D(t.x, t.y,  b.x,b.y )
        return lh/h
      },

      get lipsHeightUnit(){
        let t = this.lipTopHeightUnit
        let b = this.lipBaseHeightUnit
        return t+b
      },

      get lipsWidthUnit(){
        let w = this.faceWidth
        let L = this.points['mouth_inner_left']
        let R = this.points['mouth_inner_right']
        let lw = getDistance2D(L.x, L.y,  R.x,R.y )
        return lw/w
      },

    }

    this.assetColorMasks = {
        'neck':'skin_color',
        'ears':'skin_color',
        'face_shape':'skin_color',
        'hair_front':'hair_color',
        'hair_back':'hair_color',
        'eye_brows': 'eye_brow_color',
        'eye_brows_right': 'eye_brow_color',
        'eyes':'eye_color',
        'eyes_right':'eye_color',
        'hat': 'hat_color',
        'mouth': 'lip_color',
        'facial_hair': 'facial_hair_color',
        'facial_hair_shadow': 'facial_hair_color',
    }

    this.faceAssets = {
      confident(key){
        let feat = fa.features[key]
        if(!feat){return false}
        let o = {
          'wearing_hat':0.1,
          'bald':0.90
        }
        if (o[key]){return feat.confidence > o[key]}
        return (feat.confidence > 0.05)
      },

      get info(){return self.faceInfo},
      get features(){return fa.info.features},
      feature(key){
        if (!fa.info){return false}
        if(!fa.features[key]){return false}
        let feat = fa.features[key]

        if (feat.value === "yes" && !fa.confident(key) ) {return false}
        if (feat.value === "yes") {return true}
        if (feat.value === "no") {return false}
        if(!feat.value){return false}
        if(!feat.value === 'undefined'){return false}
        return feat.value.split(" ").join("_")
      },
      f(key){return fa.feature(key)},

      get default_hair_color() {return (fa.caucasian)? '4b2f25' : '27231F'},

      get gender() { return f('gender')},
      get female() {return this.gender === 'female'},
      get male() {return this.gender === 'male'},



      get head_shape() {
        let s = f('head_shape')
        //counter pitch distortion on chin shape !! pitch detection not reliable
        if (f('pitch') < -3){
          let map = {'extra_heart':'extra_heart', 'heart':'heart', 'average':'average', 'rect':'rect', 'extra_rect':'rect' }
          s = map[s]
        }
        return s
      },

      get m_cheeks(){ return (fa.female && f('high_cheekbones')) ? 'high' : false},
      get m_eyebrow_arched() { return f('arched_eyebrows') ? 'arched' : 'straight'},
      get m_eye_makeup() { return (f('heavy_makeup') || (fa.asian && fa.female)) ? '_makeup' : ''},
      get m_eyebrow_thickness() {
        let size =f('eyebrows_size')
        let female_map = {'extra_thick':'thick', 'thick':'thick', 'average':'average', 'extra_thin':'thin', 'thin':'thin'}
        let male_map = {'extra_thick':'extra_thick', 'thick':'thick', 'average':'thick', 'thin':'average', 'extra_thin':'thin', }
        let map = (fa.female)? female_map : male_map
        if (!size || !map[size] ){return 'average'}
        return map[size]
      },

      get m_eyebrow_offsetY(){return FaceGeoModel.y_brow},
      get m_eyebrow_offsetX(){return FaceGeoModel.x_brow},
      get m_eye_offsetX(){return FaceGeoModel.x_eye},

      get m_nose_width() {
        let size =f('nose_width')
        let map = {'extra wide':'wide', 'wide':'wide', 'average':'average', 'narrow':'narrow', 'extra narrow':'narrow'}
        if (!size || !map[size] ){return 'average'}
        return map[size]
      },
      get m_lip_height() {
        let lipUnit = fg.lipsHeightUnit
        let n = 0.1
        let diff =  lipUnit - n

        if (diff<-0.01){return 'thin' }
        if (diff>0.02){return 'thick' }

        return 'average'
      },

      get m_mouth_width() {
        let muw = fg.lipsWidthUnit
        let n = 0.312
        let delta = muw-n
        //delta *= 0.5
        delta = clamp(delta, -0.1, 0.1)

        return 1+ delta
      },

      get m_eye_shape(){
        let s = f('eyes_shape')
        let map_asian = {
          'extra_round':'asian_round',
          'round': 'asian_average',
          'average': 'asian_average',
          'thin':'asian_thin',
          'extra_thin':'asian_thin'
        }
        //TODO map asian eyes to different shape asset (not different height-range)
        if (f('race') === 'asian'){ s = map_asian[s]}
        return s
      },

      get m_chin_height() {
        // let chu = fg.chinHeightUnit
        // let n = 0.23
        // let delta = chu-n
        // delta *= 0.5
        // delta = clamp(delta, -0.01, 0.03)
        return 1 //1+ delta
      },

      get m_face_width() {
        return FaceGeoModel.scaleX_face
      },

      get m_eye_rotation(){
        let r = FaceGeoModel.rot_eye
        r = (r/ TAU)* 360

        if(fa.female)r += 7
        if(!fa.female)r += 4

        return r
      },

      get m_eye_brows_rotation(){
        let r = FaceGeoModel.rot_brow
          r = (r/ TAU)* 360

       if (fa.female && f('arched_eyebrows')){r+=8}
        if (fa.female && !f('arched_eyebrows')){r+=3}
        if (!fa.female){r-=2}


        //console.log('eyebrow rotation check:', f('eyebrows_corners'), r)
        return r
      },

      //get m_eye_size(){ return 1.0}, //temp for asian eye variant

      get m_eye_size(){ return (fa.female)? 1.0 : 1.05}, //temp for asian eye variant

      get m_color_hair(){
        let hex = f('color_hair')
        let hat = f('wearing_hat')
        let blond_color = 'baa777'

        if (hat && f('hair_color_type') === 'blond'){return blond_color}
        if (hat && f('blond_hair')){return blond_color}
        //if (f('blond_hair')){return blond_color}
        if (hat){ return fa.default_hair_color}
        if (!f('color_hair')){return fa.default_hair_color}
        return f('color_hair')
      },

      get m_hair_style(){
        let style = 'style2'
        if (fa.male && f('bangs')){style = 'style1'}
        if (fa.female ){style = 'style1'}

       // if (fa.male && f('hair_top') === 'thick'){style = 'style1'}
        return style
      },

      get m_hair_front(){
       if(fa.male && f('bald')){return 'none'}

        if(useAutoML && useAutoMLHair){return HairAssetModel.asset_front}

        return 'none'
       // let style = fa.m_hair_style
       // return 'hair_'+fa.gender+'_'+style+'_front'
      },

      get m_hair_back(){
        let style = fa.m_hair_style
        if(fa.male && f('bald')){return 'none'}

        if(useAutoML && useAutoMLHair){return HairAssetModel.asset_back}

        return 'none'

        // let short_hair = (f('hair_length') === 'none' || f('hair_length') === 'short')
        // if (short_hair && fa.female) {return 'none'}
        // return 'hair_'+fa.gender+'_'+style+'_back'

      },

      get aml_glasses(){
        if(!AutoMLService.currentDetection || !AutoMLService.currentDetection.glasses){return 'none'}
        return AutoMLService.getBestConfidenceKey(AutoMLService.currentDetection.glasses).key
      },

      get aml_hat(){
        if(!AutoMLService.currentDetection || !AutoMLService.currentDetection.hat){return 'none'}
        return AutoMLService.getBestConfidenceKey(AutoMLService.currentDetection.hat).key
      },



      get m_glasses()   {
        if (!f('glasses')){ return 'none'}

        let style = 'round'
        if(useAutoML && useAutoMLGlasses) {
          let A = AutoMLService
          let styleML = A.getBestConfidenceKey(A.currentDetection.glasses, ['narrow', 'rounded'])

          let map = {
            'rounded': 'round',
            'narrow': 'square',
          }
          style = map[styleML.key] || 'square'
        }

        return 'glasses_'+ this.gender + '_' + style
      },

      get m_hat()   {
        if (!f('wearing_hat')){ return 'none'}

        let style = 'beanie'
        if(useAutoML && useAutoMLHat) {
          let A = AutoMLService
          let styleML = A.getBestConfidenceKey(A.currentDetection.hat, ['cap', 'beanie'])


          let map = {
            'cap': 'cap',
            'beanie': 'beanie',
          }
          style = map[styleML.key] || '_square'
        }

        return 'hat_'+ this.gender + '_' + style
      },


      get facial_hair(){
        let c = f('beard')
        let m = f('mustache')
        let s = f('sideburns')
        let g = f('goatee')
        let thicknesses = {'none':'thin', 'short':'thin', 'thick':'thick' }
        let c_thick = thicknesses[f('hair_beard')] || 'thin'
        let m_thick = thicknesses[f('hair_mustache')]  || 'thin'

        //possibilities = 16-sum matrix of sideburns , moustache , chin , goatee

        // sideburns + moustache + chin | sideburns + chin
        if ((s && m && c) || (s && !m && c) || (s && m && !c) ){
          return 'facial_hair_full_beard_' + c_thick + '_' + fa.head_shape
        }

        // // goatee + moustache | goatee
        // if ((!s && m && !c && g ) || (!s && !m && !c  && g ) ){
        //   return 'facial_hair_goatee_mustache_' + m_thick
        // }

        // chin + moustache | chin | goatee + chin + moustache | goatee + moustache | goatee
        if ((!s && m && c) || (!s && !m && c) || (!s && m && c && g) || (!s && m && !c && g ) || (!s && !m && !c  && g )  ){
          //TODO use thin assets once provided
          return 'facial_hair_chin_mustache_' + c_thick + '_' + fa.head_shape
        }

        // moustache
        if ((!s && m && !c && !g) ){
          return 'facial_hair_mustache_' + m_thick
        }

        // // sideburns | sideburns + moustache
        // if ((s && !m && !c ) || (s && !m && !c) || (s && m && !c) ){
        //   return 'facial_hair_sideburns_' + c_thick
        // }

        return 'none'

      },

      get hat()       { return this.m_hat},
      get glasses()   { return this.m_glasses},
      get face_shape() { return 'face_shape_'+ this.gender +'_'+ this.head_shape  },
      get hair() { return 'hair_'+ this.gender +'_1' }, //TODO hair style logic
      get facial_hair_shadow() { return (f('5oclock_shadow')) ? 'shadow_'+ this.head_shape : 'none' },

      get lip_color() { return ( fa.custom_lip_color || 'b77c8a')},
      get hat_color() { return ((f('color_hair') && f('wearing_hat')))? f('color_hair') : '303030'},
      get hair_color() { return fa.m_color_hair},
      get eye_color() { return (f('color_eyes'))? f('color_eyes') : '856743'},
      //get skin_color() { return (f('color_skin'))? f('color_skin') : 'e2ab82'},
      get skin_color() { return (fa.custom_skin_color)? fa.custom_skin_color : 'e2ab82'},
      get facial_hair_color() { return (fa.custom_facial_hair_color)? fa.custom_facial_hair_color : fa.hair_color},
      get eye_brow_color() {return (fa.custom_facial_hair_color)? fa.custom_eye_brow_color : fa.hair_color},

      get eyes() { return 'eyes_'+f('gender')+'_'+ fa.m_eye_shape + fa.m_eye_makeup},
      get eyes_mask() { return 'eyes_'+f('gender')+'_'+ fa.m_eye_shape},
      get eye_brows() { return 'eyebrow_'+f('gender')+'_'+ fa.m_eyebrow_arched +'_'+fa.m_eyebrow_thickness},
      get cheeks() { return (fa.m_cheeks)? 'cheeks_'+f('gender')+'_'+ fa.m_cheeks : 'none'},
      get nose() { return 'nose_'+f('gender')+'_'+ fa.m_nose_width},
      get mouth() { return 'mouth_'+f('gender')+'_'+ fa.m_lip_height},
      get mouth_width() { return fa.m_mouth_width},
      get chin_height() { return fa.m_chin_height},
      get face_width() { return fa.m_face_width},
      get eye_brows_rotation(){ return fa.m_eye_brows_rotation},
      get eye_rotation(){ return fa.m_eye_rotation},

      get eyebrow_offsetY(){ return fa.m_eyebrow_offsetY},
      get eyebrow_offsetX(){return fa.m_eyebrow_offsetX},
      get eye_brow_scaleX(){return FaceGeoModel.scaleX_brow},

      get eye_offsetX(){return fa.m_eye_offsetX},

      get mouth_offsetY() { return FaceGeoModel.y_mouth  },
      get nose_offsetY() { return FaceGeoModel.y_nose },

      get asian(){ return (f('race') === 'asian')},
      get caucasian(){ return (f('race') === 'white')},
      get eye_size(){return fa.m_eye_size},
      get ears(){return 'ears_'+ fa.gender},
      get neck(){return 'neck_'+ fa.gender},

      get hair_front(){return fa.m_hair_front},
      get hair_back(){return fa.m_hair_back}

    }
    fa = this.faceAssets
    f = this.faceAssets.feature
    fg = this.faceGeometry


    Raf.add(this._update)
  }

  onMLDetectionReady = (e) =>{
    console.log('...onMLDetection', e)
    this.checkReady()
  }


  destroy = ()=> {
    Raf.remove(this._update)
  }

  _update = ()=> {

  }

  _resize = (w, h)=> {
  }

  get state(){ return this._state }
  set state(state){
    this._state = state
    this.emitIfChanged('state')
  }

  get deviceOK (){ return (window.File && window.FileReader && window.FileList && window.Blob)}

  testAssetList(){
    let AssetListCheck = {
      OK:{},
      FAIL:{}
    }

    for (let s of AssetList){

      let i = new Image;
      i.imgID = s
      try {
        i.onload = function() {AssetListCheck.OK[this.imgID]=this.src}
        i.onError = function() {AssetListCheck.FAIL[this.imgID]=this.src}
        i.src = require('@/assets/img/feature_assets/'+s+'.png');
      } catch (error){ AssetListCheck.FAIL[s]=true}
    }

    for (let s of AssetListMasks){

      let i = new Image;
      i.imgID = s
      try {
        i.onload = function() {AssetListCheck.OK[this.imgID]=this.src}
        i.onError = function() {AssetListCheck.FAIL[this.imgID]=this.src}
        i.src = require('@/assets/img/feature_assets/'+s+'.png');
      } catch (error){ AssetListCheck.FAIL[s]=true}
    }




    console.log("ASSETS TEST (LOAD ALL): ", AssetListCheck)
  }

  uploadImage(name, data_url, w, h){
    var prefix = ';base64,';
    var idx = data_url.indexOf(prefix);
    if (idx >= 0) {
      this.faceImage.base64_data = data_url.substring(idx + prefix.length);
      this.faceImage.imageData = data_url
      this.faceImage.width = w;
      this.faceImage.height = h;
      this.faceImage.Image = new Image(this.faceImage.width, this.faceImage.height);
      this.faceImage.Image.src = data_url;
      this.faceImage.name = name


      this.setFaceInfo(null)
      var flags = 'bestface,propoints,classifiers,extended'
      this.uploadBetaFaceImage(name, this.faceImage.base64_data, flags)

      //if(useAutoML){AutoMLService.getDetection(name, this.faceImage.base64_data, ML_MODEL_KEYS.GLASSES)}

    }
  }

  setFaceThumbSrc(src){
    this.faceThumbSrc = src || "";
    this.emit("faceThumbSrc", this.faceThumbSrc)
  }

  setFaceInfo(info, xmlString){
    this.faceInfo = info
    this.xmlString = xmlString
    this.__info = info

    this.setFaceThumbSrc("")

    if (info){
      this.getSkinColor()
      this.getLipColor()
      this.getFacialHairColor()
      this.getEyeBrowColor()
    }

    this.emit(EVENTS.faceInfo, this.faceInfo)
    this.emit(EVENTS.xmlString, this.xmlString)


    this.state = STATE.NO_DATA
    if (!info){return}

    this.state = STATE.DATA

    console.log('assets', this.faceAssets)
    console.log('faceGeometry', this.faceGeometry)

    this.getBetaFaceThumbImage(info.face_uid);
  }

  // UTILS PARSING
  parseBetaFaceImageInfo( xmlString) {
    this._xmlString = xmlString
    this.__info = this.getBetaFaceOutputInfo(xmlString);

    let name = this.faceImage.name
    let fe = this.__info.features
    let female = (fe.gender.value === 'female')
    let hair = useAutoMLHair
    let glasses = (fe.glasses.value === "yes") && useAutoMLGlasses
    let hat = (fe.wearing_hat.value === "yes") && useAutoMLHat


    this.waitForAutoML = useAutoML && (hair || glasses || hat)
    //let detect
    if(useAutoML && this.waitForAutoML){
      AutoMLService.getDetections(name, this.faceImage.base64_data, female, {hair, hat, glasses})
    }

    this.checkReady()
  }

  checkReady(){
    let betafaceOK = (this.__info && this.__info.features && this.__info.features.gender)

    let ml_detection = AutoMLService.currentDetection
    let automlOK = (useAutoML && this.waitForAutoML)? (ml_detection && ml_detection.ready) : true

    let ok = (betafaceOK && automlOK)

    if (ok){
      let xmlString = this.__xmlString
      let info = this.__info
      let female = info.features.gender.value === "female"

      if(useAutoML && this.waitForAutoML){
        HairAssetModel.setData(female,AutoMLService.currentDetection.hair.payload)
      }


      this.faceGeoModel.setGeo( info.geoStraight, info.geoBasicStraight, female )
      this.setFaceInfo(info, xmlString);
    }
  }

  getBetaFaceOutputInfo(xmlString){
    var xmlDocRoot = $.parseXML(xmlString);
    var xml = $(xmlDocRoot).children("BetafaceImageInfoResponse");

    var xmlDoc = $(xml).children("faces");

    //no face found
    if(!xmlDoc || $(xmlDoc).children("FaceInfo").length<1){return null}

    var mainFace = $($(xmlDoc).children("FaceInfo")[0])
    var pointsXML = mainFace.children("points").children();
    var tags = mainFace.children("tags").children();

    var face_uid = mainFace.children("uid").text();
    var image_uid = mainFace.children("image_uid").text();

    var faceBounds={
      score : parseFloat($(mainFace).children("score").text()),
      x : parseFloat($(mainFace).children("x").text()),
      y : parseFloat($(mainFace).children("y").text()),
      width : parseFloat($(mainFace).children("width").text()),
      height : parseFloat($(mainFace).children("height").text()),
      angle : parseFloat($(mainFace).children("angle").text()),
    }


    //PARSE POINTS
    var pointsMap = {}
    var points = []
    pointsXML.each(function () {
      let key = $(this).children("name").text().split(" ").join("_");
      let keyType = $(this).children("type").text().split(" ").join("_");
      let x = parseFloat($(this).children("x").text());
      let y = parseFloat($(this).children("y").text());
      let  p  ={x,y, name:key, keyType:keyType}
        pointsMap[key]=p;
        points.push[p]
    })

    let radian = -(faceBounds.angle/360) * TAU
    let LE = pointsMap['eye_left_inner']
    let RE = pointsMap['eye_right_inner']
    let chin = pointsMap['chin_bottom']
    let midEyePoint = getLerp2D(LE.x, LE.y, RE.x, RE.y, 0.5)
    //let eyeChinDistancePX = getDistance2D(midEyePoint.x, midEyePoint.y , chin.x, chin.y  )
    //let tearDuctDistancePX = getDistance2D(LE.x, LE.y , RE.x, RE.y  )


    let geo = {}
    let geoType = {}
    let geoBasic = {}
    //upright transformed - origin center of eyes
    let geoStraight = {}
    let geoTypeStraight = {}
    let geoBasicStraight = {}

    let o = pointsMap
    for(let k in o) {
      //console.log(this)
      let basic = (k.indexOf('basic')> -1)
      let  p = o[k]
      let name = k
      let keyType = p.keyType
      let x = p.x
      let y = p.y

      let ps = rotatePoint2D({x,y},midEyePoint,radian)
      ps.x = ps.x - midEyePoint.x
      ps.y = ps.y - midEyePoint.y
      ps = Object.assign(ps, {name, keyType})

      if (basic){
        geoBasic[k]=p
        geoBasicStraight[k] = ps
      }else{
        geo[k]=p
        geoStraight[k] = ps
      }

      geoTypeStraight[keyType] = ps;
      geoType[keyType]=p;
    }



    //get feature detect data
    var features = {}
    tags.each(function () {
      var key = $(this).children("name").text().split(" ").join("_");
      var value = {
        confidence: $(this).children("confidence").text(),
        value: $(this).children("value").text()
      }
      features[key]=value;
    })
    features.roll = {value:faceBounds.angle, confidence:1}


    console.log('faceBounds', faceBounds)
    console.log('geo', geo)
    console.log('geoType', geoType)
    console.log('features', features)



    return {
      face_uid,
      image_uid,
      faceBounds,

      points,
      pointsMap,

      geoBasic,
      geo,
      geoType,

      geoStraight,
      geoBasicStraight,
      geoTypeStraight,
      midEyePoint,
      features
    }
  }


  getLipColor(){
    let geo = this.faceInfo.geo
    let I = this.faceImage

    let a1 = geo['mouth_inner_bottom']
    let a2 = geo['mouth_bottom']
    let  p1 = getLerpP(a1,a2, 0.7)
    let  p2 = getLerpP(a1,a2, 0.5)

    let b1 = geo['mouth_inner_left_bottom']
    let b2 = geo['mouth_left_bottom']
    let  p3 = getLerpP(b1,b2, 0.7)
    let  p4 = getLerpP(b1,b2, 0.5)

    let c1 = geo['mouth_inner_right_bottom']
    let c2 = geo['mouth_right_bottom']
    let  p5 = getLerpP(c1,c2, 0.7)
    let  p6 = getLerpP(c1,c2, 0.5)

    let lip_color = getColorAverage(I.Image, I.width, I.height, [ p3, p4, p5, p6])
    this.faceAssets.custom_lip_color = lip_color.hex

  }


  getSkinColor(){
    let geo = this.faceInfo.geo
    let I = this.faceImage

    //nose
    let nose = geo['nose_tip']
    let nose2 = geo['nose_bridge_bottom']

    let noseL = geo['nose_left_top_inner']
    let noseR = geo['nose_right_top_inner']

    let  p1 = getLerpP(noseL,noseR, 0.1)
    let  p2 = getLerpP(noseL,noseR, 0.9)
    let  p3 = getLerpP(noseL,noseR, 0.12)
    let  p4 = getLerpP(noseL,noseR, 0.88)

    //cheeks
    let a1 = geo['nose_left_bottom_outer']
    let a2 = geo['chin_ear_left']

    let b1 = geo['nose_right_bottom_outer']
    let b2 = geo['chin_ear_right']

    let  p5 = getLerpP(a1,a2, 0.4)
    let  p6 = getLerpP(a1,a2, 0.42)
    let  p7 = getLerpP(b1,b2, 0.4)
    let  p8 = getLerpP(b1,b2, 0.42)

    let nose_color = getColorAverage(I.Image, I.width, I.height, [nose2,p1,p2,p3,p4])
    let cheek_color = getColorAverage(I.Image, I.width, I.height, [p5,p6,p7,p8])
    let cheek_nose_color = getColorAverage(I.Image, I.width, I.height, [nose2,p1,p2,p3,p4,p5,p6,p7,p8])
    this.faceAssets.custom_skin_color = cheek_nose_color.hex
  }

  getFacialHairColor(){
    //TODO account for no beard
    let geo = this.faceInfo.geo
    let I = this.faceImage

    //Beard check
    let mb = geo['mouth_bottom']
    let cb = geo['chin_bottom']

    let  p1 = getLerpP(mb,cb, 0.98)
    let  p2 = getLerpP(mb,cb,  0.90)
    let  p3 = getLerpP(mb,cb,  1.01)

    //Mustache check
    let nb = geo['nose_left_bottom_outer']
    let mt = geo['mouth_left_top_point_1']

    let nb2 = geo['nose_left_bottom_outer']
    let mt2 = geo['mouth_right_top_point_1']

    let p4 = getLerpP(nb,mt, 0.6)
    let p5 = getLerpP(nb2,mt2, 0.6)

    let beard_color = getColorAverage(I.Image, I.width, I.height, [p1, p2, p3])
    let mustache_color = getColorAverage(I.Image, I.width, I.height, [p4, p5])
    let mustache_beard_color = getColorAverage(I.Image, I.width, I.height, [p1,p2, p3, p4,p5])

    let beard = (f('beard') && f('hair_beard') !== 'none')
    let mustache = (f('mustache') && f('hair_mustache') !== 'none')

    //use both unless beard or mustache not detected.
    let color = mustache_beard_color
    if (beard && !mustache){ color = beard_color}
    if (!beard && mustache){ color = mustache_color}

    this.faceAssets.custom_facial_hair_color = color.hex
  }

  getEyeBrowColor(){
    let geo = this.faceInfo.geo
    let I = this.faceImage

    //LEFT
    let a1 = geo['eyebrow_left_top']
    let a2 = geo['eyebrow_left_bottom']

    let a3 = geo['eyebrow_left_top_inner']
    let a4 = geo['eyebrow_left_bottom_inner']

    //RIGHT
    let b1 = geo['eyebrow_right_top']
    let b2 = geo['eyebrow_right_bottom'] || geo['eyebrow_right_botom'] //misspelt, assume will change

    let b3 = geo['eyebrow_right_top_inner']
    let b4 = geo['eyebrow_right_bottom_inner'] || geo['eyebrow_rigth_bottom_inner'] //misspelt, assume will change

    let  p1 = getLerpP(a1, a2, 0.5)
    let  p2 = getLerpP(a1, a2, 0.55)
    let  p3 = getLerpP(b1, b2, 0.5)
    let  p4 = getLerpP(b1, b2, 0.55)

    let  p5 = getLerpP(a3, a4, 0.5)
    let  p6 = getLerpP(a3, a4, 0.55)
    let  p7 = getLerpP(b3, b4, 0.5)
    let  p8 = getLerpP(b3, b4, 0.55)

    let brow_color = getColorAverage(I.Image, I.width, I.height, [p1,p2, p3, p4, p5, p6, p7, p8])

    this.faceAssets.custom_eye_brow_color = brow_color.hex
  }


  //WEB SERVICE UTILS--------------------------------------------------------------//

  uploadBetaFaceImage = (name, image_data,  detection_flags) => {
    this.state = STATE.WAITING
    var msg = '<?xml version="1.0" encoding="utf-8"?><ImageRequestBinary xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">' +
      '<api_key>'+API.KEY+'</api_key><api_secret>'+API.SECRET+'</api_secret>' +
      '<detection_flags>' + detection_flags + '</detection_flags>' +
      '<imagefile_data>' + image_data + '</imagefile_data>' +
      '<original_filename>' + name + '</original_filename>' +
      '</ImageRequestBinary>';
    $.support.cors = true;
    $.ajax({
      crossDomain: true,
      url: this.getAPIURL() + 'UploadNewImage_File',
      type: 'post',
      contentType: 'application/xml',
      processData: false,
      data: msg,
      dataType: 'xml',
      success: (data, textStatus, jqXHR) => {
        var xmlDocRoot = $.parseXML(jqXHR.responseText);
        var xmlDoc = $(xmlDocRoot).children("BetafaceImageResponse");
        var int_response = parseInt($(xmlDoc).children("int_response").text());
        var string_response = $(xmlDoc).children("string_response").text();
        if (int_response == 0) {
          var img_uid = $(xmlDoc).children("img_uid").text();
          this.getBetaFaceImageInfo(img_uid);
        }
        else {
          //error
          console.info(int_response);
          console.info(string_response);
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        console.info(textStatus);
      }
    });
  }

  getAPIURL = () => {
    return process.env.VUE_APP_SSL_ENABLED === "true" ? "https://www.betafaceapi.com/service_ssl.svc/" : "http://www.betafaceapi.com/service.svc/";
  }

  getBetaFaceImageInfo = (image_uid) => {
    var msg = '<?xml version="1.0" encoding="utf-8"?><ImageInfoRequestUid>' +
      '<api_key>'+API.KEY+'</api_key><api_secret>'+API.SECRET+'</api_secret>'  +
      '<img_uid>' + image_uid + '</img_uid></ImageInfoRequestUid>';

    $.support.cors = true;
    $.ajax({
      crossDomain: true,
      url:  this.getAPIURL() + 'GetImageInfo',
      type: 'post',
      contentType: 'application/xml',
      processData: false,
      data: msg,
      dataType: 'xml',
      success:  (data, textStatus, jqXHR) => {
        var xmlDocRoot = $.parseXML(jqXHR.responseText);
        var xmlDoc = $(xmlDocRoot).children("BetafaceImageInfoResponse");
        var int_response = parseInt($(xmlDoc).children("int_response").text());
        var string_response = $(xmlDoc).children("string_response").text();
        if (int_response == 1) {
          //image is in the queue( !!POLLING)
          setTimeout(()=> { this.getBetaFaceImageInfo(image_uid); }, 500);
        }
        else if (int_response == 0) {
          //image processed
          this.parseBetaFaceImageInfo(jqXHR.responseText);
        }
        else {
          //error
          console.info(int_response);
          console.info(string_response);
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        console.info(textStatus);
      }
    });
  }

  getBetaFaceThumbImage = (face_uid) => {
    var msg = '<?xml version="1.0" encoding="utf-8"?><FaceRequestId>' +
        '<api_key>' + API.KEY + '</api_key><api_secret>' + API.SECRET + '</api_secret>' +
        '<face_uid>' + face_uid + '</face_uid></FaceRequestId>';

    $.support.cors = true;
    $.ajax({
      crossDomain: true,
      url: this.getAPIURL() + 'GetFaceImage',
      type: 'post',
      contentType: 'application/xml',
      processData: false,
      data: msg,
      dataType: 'xml',
      success: (data, textStatus, jqXHR) => {
        var xmlDocRoot = $.parseXML(jqXHR.responseText);
        var xmlDoc = $(xmlDocRoot).children("BetafaceFaceImageResponse");
        var int_response = parseInt($(xmlDoc).children("int_response").text());
        var string_response = $(xmlDoc).children("string_response").text();
        if (int_response == 0) {
          var face_uid = $(xmlDoc).children("uid").text();
          var face_image = $(xmlDoc).children("face_image").text();
          var data_url = 'data:image/jpeg;base64,' + face_image;
          // Render face image.
          //$('.thumb_crop').attr('src', data_url)
          this.setFaceThumbSrc(data_url)
        } else {
          //error
          console.info(int_response);
          console.info(string_response);
        }
      },
      error: function (jqXHR, textStatus, errorThrown) {
        console.info(textStatus);
      }
    });
  }
}

export default new FaceFeatureService()